import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import chalk from 'chalk';
import ora from 'ora';
import { WorkflowManager } from '../core/workflow-manager';
import { logger } from '../utils/logger';
import Table from 'cli-table3';
import inquirer from 'inquirer';

export function createDesignCommand(): Command {
  const command = new Command('design');
  
  command
    .description('管理设计文档')
    .addCommand(createGenerateCommand())
    .addCommand(createListCommand())
    .addCommand(createViewCommand())
    .addCommand(createApproveCommand());
  
  return command;
}

function createGenerateCommand(): Command {
  const command = new Command('generate');
  
  command
    .description('生成设计文档')
    .argument('[prd-path]', 'PRD 文档路径')
    .option('-t, --template <template>', '使用的模板 (full/simple)', 'simple')
    .option('-o, --output <path>', '输出路径')
    .option('--title <title>', '设计文档标题')
    .option('--author <author>', '作者', '@architect')
    .action(async (prdPath, options) => {
      const spinner = ora('生成设计文档...').start();
      
      try {
        const manager = new WorkflowManager();
        
        // 如果没有提供 PRD 路径，交互式选择
        if (!prdPath) {
          const prdDir = path.join(process.cwd(), '.workflow/docs/prd');
          if (await fs.pathExists(prdDir)) {
            const files = await fs.readdir(prdDir);
            const prdFiles = files.filter(f => f.endsWith('.md'));
            
            if (prdFiles.length === 0) {
              spinner.fail('没有找到 PRD 文档');
              logger.info('请先使用 "workflow new" 命令生成 PRD');
              return;
            }
            
            const { selectedPrd } = await inquirer.prompt([
              {
                type: 'list',
                name: 'selectedPrd',
                message: '选择 PRD 文档:',
                choices: prdFiles.map(f => ({
                  name: f,
                  value: path.join(prdDir, f)
                }))
              }
            ]);
            
            prdPath = selectedPrd;
          } else {
            spinner.fail('PRD 目录不存在');
            logger.info('请先使用 "workflow new" 命令生成 PRD');
            return;
          }
        }
        
        // 生成设计文档
        const { doc, content } = await manager.generateDesignDoc(prdPath, {
          template: options.template,
          title: options.title,
          author: options.author
        });
        
        spinner.succeed('设计文档生成成功！');
        
        // 显示信息
        console.log('\n' + chalk.bold('设计文档信息:'));
        console.log(chalk.gray('ID:'), doc.id);
        console.log(chalk.gray('标题:'), doc.title);
        console.log(chalk.gray('路径:'), doc.path);
        console.log(chalk.gray('关联 PRD:'), doc.prdId || '无');
        
        if (doc.taskIds && doc.taskIds.length > 0) {
          console.log(chalk.gray('创建任务数:'), doc.taskIds.length);
        }
        
        // 如果指定了输出路径，复制文件
        if (options.output) {
          await fs.copy(doc.path, options.output);
          console.log(chalk.gray('已复制到:'), options.output);
        }
        
        console.log('\n' + chalk.green('下一步:'));
        console.log('1. 查看设计文档: ' + chalk.cyan(`workflow design view ${doc.id}`));
        console.log('2. 编辑设计文档: ' + chalk.cyan(`${process.env.EDITOR || 'code'} ${doc.path}`));
        console.log('3. 审批设计文档: ' + chalk.cyan(`workflow design approve ${doc.id}`));
        
      } catch (error: any) {
        spinner.fail('生成设计文档失败');
        logger.error(error.message);
      }
    });
  
  return command;
}

function createListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('列出所有设计文档')
    .option('-s, --status <status>', '按状态筛选 (draft/review/approved/archived)')
    .action(async (options) => {
      try {
        const manager = new WorkflowManager();
        const docs = await manager.listDesignDocs();
        
        if (docs.length === 0) {
          logger.info('没有找到设计文档');
          return;
        }
        
        // 筛选
        let filteredDocs = docs;
        if (options.status) {
          filteredDocs = docs.filter(d => d.status === options.status);
        }
        
        // 创建表格
        const table = new Table({
          head: ['ID', '标题', '状态', 'PRD', '任务数', '创建时间'],
          style: { head: ['cyan'] }
        });
        
        for (const doc of filteredDocs) {
          const statusColor = {
            draft: 'gray',
            review: 'yellow',
            approved: 'green',
            archived: 'dim'
          }[doc.status] || 'white';
          
          table.push([
            doc.id,
            doc.title,
            chalk[statusColor](doc.status),
            doc.prdId || '-',
            doc.taskIds?.length || 0,
            new Date(doc.createdAt).toLocaleDateString()
          ]);
        }
        
        console.log(table.toString());
        console.log(chalk.gray(`\n共 ${filteredDocs.length} 个设计文档`));
        
      } catch (error: any) {
        logger.error('列出设计文档失败:', error.message);
      }
    });
  
  return command;
}

function createViewCommand(): Command {
  const command = new Command('view');
  
  command
    .description('查看设计文档')
    .argument('<id>', '设计文档 ID')
    .action(async (id) => {
      try {
        const manager = new WorkflowManager();
        const result = await manager.getDesignDoc(id);
        
        if (!result) {
          logger.error('设计文档不存在');
          return;
        }
        
        const { doc, content } = result;
        
        // 显示文档信息
        console.log(chalk.bold.cyan('\n设计文档信息:'));
        console.log(chalk.gray('ID:'), doc.id);
        console.log(chalk.gray('标题:'), doc.title);
        console.log(chalk.gray('状态:'), doc.status);
        console.log(chalk.gray('关联 PRD:'), doc.prdId || '无');
        console.log(chalk.gray('创建时间:'), new Date(doc.createdAt).toLocaleString());
        console.log(chalk.gray('更新时间:'), new Date(doc.updatedAt).toLocaleString());
        
        if (doc.taskIds && doc.taskIds.length > 0) {
          console.log(chalk.gray('关联任务:'), doc.taskIds.join(', '));
        }
        
        console.log(chalk.bold.cyan('\n文档内容:'));
        console.log(chalk.gray('─'.repeat(80)));
        console.log(content);
        console.log(chalk.gray('─'.repeat(80)));
        
      } catch (error: any) {
        logger.error('查看设计文档失败:', error.message);
      }
    });
  
  return command;
}

function createApproveCommand(): Command {
  const command = new Command('approve');
  
  command
    .description('审批设计文档')
    .argument('<id>', '设计文档 ID')
    .option('-s, --status <status>', '设置状态 (review/approved/archived)', 'approved')
    .action(async (id, options) => {
      try {
        const manager = new WorkflowManager();
        const designDocManager = (manager as any).designDocManager;
        
        const success = await designDocManager.updateStatus(id, options.status);
        
        if (success) {
          logger.success(`设计文档状态已更新为: ${options.status}`);
        } else {
          logger.error('设计文档不存在');
        }
        
      } catch (error: any) {
        logger.error('更新设计文档状态失败:', error.message);
      }
    });
  
  return command;
}